<template>
    <section class="member-import">
        <!-- Two steps, because the task genuinely is a sequence: the template has to be
             downloaded and filled before there is anything to upload. -->
        <div class="card mb-4">
            <div class="import-step">
                <div class="step-marker" aria-hidden="true">1</div>
                <div class="step-body">
                    <h2 class="step-title">Get the template</h2>
                    <p class="step-hint">
                        Fill it in Excel. Example rows are grey and italic — type over them or leave them,
                        they are skipped automatically during import.
                    </p>

                    <button
                        class="template-link"
                        type="button"
                        :disabled="downloading"
                        @click="downloadTemplate"
                    >
                        <i
                            class="pi"
                            :class="downloading ? 'pi-spinner is-spinning' : 'pi-download'"
                            aria-hidden="true"
                        ></i>
                        <span>{{ downloading ? 'Preparing…' : 'Download template' }}</span>
                    </button>

                    <!-- Grouped, not colour-coded in source order: the question this answers
                         is "which ones must I fill in", and that has to be scannable. -->
                    <details class="file-spec">
                        <summary>Columns the file must have</summary>
                        <p class="spec-caption">Required — every one of these</p>
                        <ul class="spec-columns">
                            <li v-for="column in requiredColumns" :key="column.header" class="is-required">
                                {{ column.header }}
                            </li>
                        </ul>
                        <p class="spec-caption">Optional</p>
                        <ul class="spec-columns">
                            <li v-for="column in optionalColumns" :key="column.header">
                                {{ column.header }}
                            </li>
                        </ul>
                        <p class="spec-limits">
                            Up to {{ maxRows.toLocaleString('en-US') }} rows and
                            {{ maxFileSizeMb }} MB per file, .xlsx only.
                        </p>
                    </details>
                </div>
            </div>

            <div class="import-step">
                <div class="step-marker" aria-hidden="true">2</div>
                <div class="step-body">
                    <h2 class="step-title">Upload the filled file</h2>
                    <p class="step-hint">Every member in the file is registered to the event you pick here.</p>

                    <div class="inline-row inline-row--top">
                        <div class="field">
                            <label for="import-event">Register to event</label>
                            <select id="import-event" v-model="selectedEventId" class="form-control">
                                <option v-for="event in events" :key="event.id" :value="event.id">
                                    {{ event.name }}
                                </option>
                            </select>
                        </div>

                        <div class="field">
                            <label for="import-file">Excel file</label>
                            <input
                                id="import-file"
                                ref="fileInput"
                                type="file"
                                accept=".xlsx"
                                class="form-control"
                                @change="onFileSelected"
                            >
                        </div>
                    </div>

                    <div class="option-row">
                        <input id="send-account-email" type="checkbox" v-model="sendAccountEmail">
                        <div>
                            <label for="send-account-email">Email login details to members who have a Login ID</label>
                            <p>
                                Sends a real email as soon as the import finishes.
                                Members without a Login ID are never emailed.
                            </p>
                        </div>
                    </div>

                    <div class="action-row">
                        <button
                            class="btn btn-primary"
                            type="button"
                            :disabled="!canImport"
                            aria-describedby="import-blocker"
                            @click="uploadFile"
                        >
                            <i
                                class="pi"
                                :class="importing ? 'pi-spinner is-spinning' : 'pi-upload'"
                                aria-hidden="true"
                            ></i>
                            <span>{{ importing ? 'Importing…' : 'Import members' }}</span>
                        </button>
                        <span id="import-blocker" class="action-reason">{{ importBlocker }}</span>
                    </div>
                </div>
            </div>
        </div>

        <div v-if="importResult" class="card results-card" aria-live="polite">
            <div class="results-head">
                <div>
                    <h2 class="results-title">
                        {{ importResult.successCount }} of {{ plural(importResult.totalRows, 'row') }} imported
                    </h2>
                    <p class="results-meta">
                        <span v-if="importedFileName">From {{ importedFileName }}</span><span
                            v-if="importResult.eventName"
                        >{{ importedFileName ? ' · ' : '' }}Registered to {{ importResult.eventName }}</span>
                    </p>
                </div>
                <button
                    v-if="importResult.errorCount > 0"
                    class="btn btn-outline-secondary btn-sm"
                    type="button"
                    @click="downloadErrorReport"
                >
                    <i class="pi pi-download" aria-hidden="true"></i>
                    <span>Download the {{ plural(importResult.errorCount, 'failed row') }}</span>
                </button>
            </div>

            <!-- The counts double as the filter: after an import the only rows that
                 need attention are the failures, so they are selected by default. -->
            <div class="filter-tiles" role="group" aria-label="Filter rows by outcome">
                <button
                    v-for="tile in resultTiles"
                    :key="tile.key"
                    type="button"
                    class="tile"
                    :class="[tile.modifier, { 'is-active': resultFilter === tile.key }]"
                    :disabled="tile.count === 0 && tile.key !== 'all'"
                    :aria-pressed="resultFilter === tile.key"
                    @click="resultFilter = tile.key"
                >
                    <span class="tile-value">{{ tile.count }}</span>
                    <span class="tile-label">{{ tile.label }}</span>
                </button>
            </div>

            <div class="import-table-wrap">
                <table class="table import-table">
                    <thead>
                    <tr>
                        <th scope="col" class="row-column">Row</th>
                        <th scope="col">Name</th>
                        <th scope="col" class="status-column">Status</th>
                        <th scope="col">What happened</th>
                        <th scope="col" class="login-column">Login ID</th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr
                        v-for="(row, index) in filteredDetails"
                        :key="index"
                        :class="rowStatusClass(row.status)"
                    >
                        <td>{{ row.rowNumber }}</td>
                        <td>{{ row.name || '—' }}</td>
                        <td>
                            <span class="status-badge" :class="rowStatusClass(row.status)">
                                {{ formatStatus(row.status) }}
                            </span>
                        </td>
                        <td class="message-cell">{{ row.message || '—' }}</td>
                        <td class="login-id" :class="{ 'is-empty': !row.username }">{{ row.username || '—' }}</td>
                    </tr>
                    </tbody>
                </table>
            </div>
        </div>
    </section>
</template>

<script>
import {defineComponent} from 'vue';
import apiService from '@/services/ApiService';

// Mirrors ExcelUtil.ImportColumns on the API so the file contract is visible
// before an upload fails on it.
const TEMPLATE_COLUMNS = [
    {header: 'Member Name', required: true},
    {header: 'Gender', required: true},
    {header: 'Preferred Badge Name', required: true},
    {header: 'Designation', required: true},
    {header: 'Company Name', required: true},
    {header: 'Company Description', required: false},
    {header: 'Address', required: true},
    {header: 'City Name', required: false},
    {header: 'Country Name', required: false},
    {header: 'Email', required: true},
    {header: 'Phone Number', required: false},
    {header: 'Fax', required: false},
    {header: 'Website', required: false},
    {header: 'Other Spices', required: false},
    {header: 'Food Type', required: false},
    {header: 'Shirt Size', required: false},
    {header: 'Payment Method', required: false},
    {header: 'Login ID', required: false}
];

export default defineComponent({
    name: 'MemberImport',
    data() {
        return {
            events: [],
            selectedEventId: '',
            sendAccountEmail: true,
            selectedFile: null,
            importing: false,
            downloading: false,
            importResult: null,
            importedFileName: '',
            resultFilter: 'all',
            templateColumns: TEMPLATE_COLUMNS,
            maxRows: 5000,
            maxFileSizeMb: 5
        };
    },
    computed: {
        canImport() {
            return Boolean(this.selectedFile) && Boolean(this.selectedEventId) && !this.importing;
        },
        importBlocker() {
            if (this.importing) {
                return '';
            }
            if (!this.selectedEventId) {
                return 'Pick the event these members belong to.';
            }
            if (!this.selectedFile) {
                return 'Choose an .xlsx file to enable the import.';
            }
            return '';
        },
        requiredColumns() {
            return this.templateColumns.filter(column => column.required);
        },
        optionalColumns() {
            return this.templateColumns.filter(column => !column.required);
        },
        resultTiles() {
            const result = this.importResult;
            if (!result) {
                return [];
            }
            return [
                {key: 'all', label: 'All rows', count: result.totalRows, modifier: ''},
                {key: 'success', label: 'Succeeded', count: result.successCount, modifier: 'tile--ok'},
                {key: 'error', label: 'Failed', count: result.errorCount, modifier: 'tile--error'},
                {key: 'skipped', label: 'Skipped', count: result.skippedCount, modifier: 'tile--skipped'}
            ];
        },
        filteredDetails() {
            const details = this.importResult?.details || [];
            if (this.resultFilter === 'all') {
                return details;
            }
            return details.filter(row => this.statusKey(row.status) === this.resultFilter);
        }
    },
    mounted() {
        this.loadEvents();
    },
    methods: {
        loadEvents() {
            apiService.get('/event')
                .then(response => {
                    this.events = response.data?.data || [];
                    return apiService.get('/event/active');
                })
                .then(response => {
                    const activeEvent = response.data;
                    if (activeEvent && activeEvent.id) {
                        this.selectedEventId = activeEvent.id;
                    } else if (this.events.length > 0) {
                        this.selectedEventId = this.events[0].id;
                    }
                })
                .catch(error => {
                    this.$toast.add({
                        severity: 'error',
                        summary: 'Failed to load events',
                        detail: error.response?.data?.Message || 'An error occurred',
                        life: 5000
                    });
                });
        },
        onFileSelected(event) {
            const file = event.target.files[0];
            if (!file) {
                this.selectedFile = null;
                return;
            }

            if (!file.name.toLowerCase().endsWith('.xlsx')) {
                this.$toast.add({
                    severity: 'error',
                    summary: 'Invalid file type',
                    detail: 'Only .xlsx files are supported. Open the file in Excel and use Save As → Excel Workbook (.xlsx).',
                    life: 5000
                });
                event.target.value = '';
                this.selectedFile = null;
                return;
            }

            this.selectedFile = file;
            this.importResult = null;
        },
        uploadFile() {
            if (!this.selectedFile) {
                return;
            }

            const formData = new FormData();
            formData.append('file', this.selectedFile);
            formData.append('eventId', this.selectedEventId);
            formData.append('sendAccountEmail', this.sendAccountEmail);

            const fileName = this.selectedFile.name;
            this.importing = true;
            apiService.post('/member/import', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            })
                .then(response => {
                    this.importResult = response.data;
                    this.importedFileName = fileName;
                    this.resultFilter = response.data.errorCount > 0 ? 'error' : 'all';
                    this.$toast.add({
                        severity: 'success',
                        summary: 'Import completed',
                        detail: `${response.data.successCount} member(s) imported successfully`,
                        life: 5000
                    });
                })
                .catch(error => {
                    this.$toast.add({
                        severity: 'error',
                        summary: 'Import failed',
                        detail: error.response?.data?.Message || 'An error occurred',
                        life: 5000
                    });
                })
                .finally(() => {
                    this.importing = false;
                });
        },
        async downloadTemplate() {
            this.downloading = true;
            try {
                const response = await apiService.get('/member/import/template', {
                    responseType: 'blob'
                });
                this.saveBlob(new Blob([response.data], {
                    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
                }), 'member-import-template.xlsx');
            } catch (error) {
                this.$toast.add({
                    severity: 'error',
                    summary: 'Download failed',
                    detail: error.response?.data?.Message || 'Unable to download the template.',
                    life: 5000
                });
            } finally {
                this.downloading = false;
            }
        },
        // A worklist of what went wrong, not a re-uploadable workbook: the API result
        // carries the outcome of each row, not the values that were in it.
        downloadErrorReport() {
            const failedRows = (this.importResult?.details || [])
                .filter(row => this.statusKey(row.status) === 'error');
            const csv = [
                ['Row', 'Name', 'Message'],
                ...failedRows.map(row => [row.rowNumber, row.name || '', row.message || ''])
            ]
                .map(cells => cells.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
                .join('\r\n');

            this.saveBlob(new Blob([`\uFEFF${csv}`], {type: 'text/csv;charset=utf-8'}), 'member-import-errors.csv');
        },
        saveBlob(blob, fileName) {
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = fileName;
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        },
        plural(count, noun) {
            return `${count} ${noun}${count === 1 ? '' : 's'}`;
        },
        statusKey(status) {
            if (status === 0 || status === 'Success') return 'success';
            if (status === 1 || status === 'Error') return 'error';
            if (status === 2 || status === 'Skipped') return 'skipped';
            return '';
        },
        formatStatus(status) {
            const key = this.statusKey(status);
            if (key === 'success') return 'Success';
            if (key === 'error') return 'Error';
            if (key === 'skipped') return 'Skipped';
            return status;
        },
        rowStatusClass(status) {
            const key = this.statusKey(status);
            return key ? `row-${key}` : '';
        }
    }
});
</script>

<style scoped>
/* Steps */
.import-step {
    display: grid;
    grid-template-columns: 2rem 1fr;
    gap: 1rem;
    padding: 1.25rem 1.5rem;
}

.import-step + .import-step {
    border-top: 1px solid #eef0f4;
}

.step-marker {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 2rem;
    height: 2rem;
    border-radius: 50%;
    background: #eef2ff;
    color: #3b5bdb;
    font-size: 0.85rem;
    font-weight: 700;
}

.step-title {
    margin: 0;
    color: #1f2937;
    font-size: 1rem;
    font-weight: 700;
    letter-spacing: -0.01em;
}

.step-hint {
    margin: 0.2rem 0 0.9rem;
    color: #6b7280;
    font-size: 0.875rem;
}

/* Fields */
.inline-row {
    display: flex;
    flex-wrap: wrap;
    align-items: flex-end;
    gap: 0.75rem 1rem;
}

.inline-row--top {
    align-items: flex-start;
}

.field {
    flex: 1 1 18rem;
    max-width: 22rem;
}

/* PrimeFlex ships its own .field utility carrying margin-bottom: 1rem, and this
   component's class name collides with it. Flex alignment works on the margin
   box, so that inherited margin left the select sitting 16px above the button it
   was supposed to line up with. Stacked forms elsewhere want that spacing; a row
   does not. */
.inline-row .field {
    margin-bottom: 0;
}

.field label {
    display: block;
    margin-bottom: 0.3rem;
    color: #374151;
    font-size: 0.875rem;
    font-weight: 600;
}

/* One height for every control in these rows. 44px rather than Bootstrap's 38px
   because the box has to clear the browser's own Choose File control, which was
   otherwise pressed against the bottom border of its field; how tall that
   control is depends on the engine, so the height is set rather than derived.
   Pinning .btn and .form-control together also stops them being sized by two
   different rules — a formula for one, the content box for the other. */
.inline-row > .btn,
.inline-row .form-control {
    height: 44px;
    padding-top: 0.375rem;
    padding-bottom: 0.375rem;
}

.template-link {
    display: inline-flex;
    align-items: center;
    gap: 0.35rem;
    padding: 0;
    border: none;
    background: none;
    color: #3b5bdb;
    font-size: 0.875rem;
    font-weight: 600;
    cursor: pointer;
}

.template-link:hover:not(:disabled) {
    text-decoration: underline;
}

.template-link:focus-visible {
    outline: 2px solid #3b5bdb;
    outline-offset: 2px;
}

.template-link:disabled {
    color: #6b7280;
    cursor: not-allowed;
}

.template-link .pi {
    margin-right: 0;
}

/* File spec */
.file-spec {
    margin-top: 0.9rem;
}

.file-spec summary {
    color: #3b5bdb;
    cursor: pointer;
    font-size: 0.875rem;
    font-weight: 600;
}

.spec-caption {
    margin: 0.75rem 0 0.35rem;
    color: #6b7280;
    font-size: 0.78rem;
    font-weight: 600;
    letter-spacing: 0.02em;
    text-transform: uppercase;
}

.spec-columns {
    display: flex;
    flex-wrap: wrap;
    gap: 0.35rem;
    margin: 0.6rem 0 0;
    padding: 0;
    list-style: none;
}

.spec-columns li {
    padding: 0.15rem 0.45rem;
    border-radius: 4px;
    background: #f3f4f6;
    color: #374151;
    font-size: 0.78rem;
}

.spec-columns li.is-required {
    background: #eef2ff;
    color: #3b5bdb;
    font-weight: 600;
}

.spec-limits {
    margin: 0.6rem 0 0;
    color: #6b7280;
    font-size: 0.8rem;
}

/* Sending real email deserves more weight than a bare checkbox in the stack —
   but an accent rule carries that without adding a third nested surface. */
.option-row {
    display: flex;
    align-items: flex-start;
    gap: 0.6rem;
    max-width: 34rem;
    margin: 1rem 0 1.15rem;
    padding: 0.1rem 0 0.1rem 0.85rem;
    border-left: 3px solid #c7d2fe;
}

.option-row input {
    margin-top: 0.2rem;
}

.option-row label {
    margin: 0;
    color: #374151;
    font-size: 0.875rem;
    font-weight: 600;
}

.option-row p {
    margin: 0.15rem 0 0;
    color: #6b7280;
    font-size: 0.8rem;
}

.action-row {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.5rem 0.85rem;
}

.action-reason {
    color: #6b7280;
    font-size: 0.825rem;
}

.btn .pi {
    margin-right: 0.35rem;
}

.is-spinning {
    animation: member-import-spin 1s linear infinite;
}

@keyframes member-import-spin {
    to { transform: rotate(360deg); }
}

@media (prefers-reduced-motion: reduce) {
    .is-spinning {
        animation: none;
    }

    .tile {
        transition: none;
    }
}

/* Results */
.results-card {
    padding: 1.25rem 1.5rem;
}

.results-head {
    display: flex;
    flex-wrap: wrap;
    align-items: baseline;
    justify-content: space-between;
    gap: 0.5rem 1rem;
    margin-bottom: 0.9rem;
}

.results-title {
    margin: 0;
    color: #1f2937;
    font-size: 1.05rem;
    font-weight: 700;
}

.results-meta {
    margin: 0.15rem 0 0;
    color: #6b7280;
    font-size: 0.85rem;
}

.filter-tiles {
    display: flex;
    flex-wrap: wrap;
    gap: 0.6rem;
    margin-bottom: 1rem;
}

.tile {
    min-width: 7.5rem;
    padding: 0.55rem 0.9rem;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    background: #fff;
    text-align: left;
    transition: border-color 0.12s ease, background-color 0.12s ease;
}

.tile:hover:not(:disabled) {
    border-color: #c7ccd4;
    background: #fafbfc;
}

.tile:focus-visible {
    outline: 2px solid #3b5bdb;
    outline-offset: 2px;
}

.tile:disabled {
    opacity: 0.55;
    cursor: not-allowed;
}

/* Selected fills with the outcome's own tint, so the group reads as a set of
   controls rather than four stat cards with one in an error state. */
.tile.is-active {
    border-color: #c7d2fe;
    background: #eef2ff;
}

.tile.is-active.tile--ok { border-color: #a7f3d0; background: #ecfdf5; }
.tile.is-active.tile--error { border-color: #fecaca; background: #fef2f2; }
.tile.is-active.tile--skipped { border-color: #fde68a; background: #fffbeb; }

.tile-value {
    display: block;
    color: #1f2937;
    font-size: 1.25rem;
    font-weight: 700;
    line-height: 1.1;
}

.tile-label {
    color: #6b7280;
    font-size: 0.78rem;
}

.tile--ok .tile-value { color: #047857; }
.tile--error .tile-value { color: #b91c1c; }
.tile--skipped .tile-value { color: #b45309; }

.import-table-wrap {
    overflow-x: auto;
}

/* app.css centres every th; here the headers must sit over left-aligned cells. */
.import-table th {
    text-align: left;
}

.import-table td {
    vertical-align: top;
}

/* Only the message may break mid-word: a long email in it would otherwise push
   the table into a horizontal scroll and hide the column that matters most
   after a failure. Names and badges must stay intact. */
.message-cell {
    overflow-wrap: anywhere;
}

.row-column { width: 4.5rem; }
.status-column { width: 7rem; }
.login-column { width: 10rem; }


.login-id {
    color: #374151;
    font-family: SFMono-Regular, Menlo, Consolas, monospace;
    font-size: 0.82rem;
}

.login-id.is-empty {
    color: #9ca3af;
}

.status-badge {
    display: inline-block;
    white-space: nowrap;
    padding: 0.15rem 0.5rem;
    border-radius: 999px;
    font-size: 0.78rem;
    font-weight: 600;
}

.row-success .status-badge {
    background: #d1fae5;
    color: #065f46;
}

.row-error .status-badge {
    background: #fee2e2;
    color: #991b1b;
}

.row-skipped .status-badge {
    background: #fef3c7;
    color: #92400e;
}

@media (max-width: 575.98px) {
    .import-step,
    .results-card {
        padding: 1.1rem 1rem;
    }

    .import-step {
        grid-template-columns: 1.75rem 1fr;
        gap: 0.75rem;
    }

    .field {
        flex-basis: 100%;
        max-width: none;
    }

    .tile {
        flex: 1 1 40%;
        min-width: 0;
    }

    /* Let the table be as wide as it needs and scroll inside its wrapper, rather
       than squeezing the message into an unreadable ribbon. */
    .row-column,
    .status-column,
    .login-column {
        width: auto;
    }

    .message-cell {
        min-width: 11rem;
    }
}
</style>
